import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon, MessageSquareTextIcon, ArrowLeftIcon, CheckCircle2Icon, PhoneIcon, CheckIcon, ImageIcon } from 'lucide-react';
import { business, serviceAreas } from '../../data/content';
import { formatPhone, serviceOptions, useEstimateForm } from './useEstimateForm';
import { PhotoUpload } from './PhotoUpload';
type EstimateModalProps = {
  open: boolean;
  initialService?: string;
  onClose: () => void;
};
const ease = [0.23, 1, 0.32, 1] as const;
const inputBase = 'w-full rounded-lg border bg-white px-3.5 py-2.5 text-[15px] text-ink placeholder:text-ink-muted/60 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-forest/20';
function fieldClass(error?: string) {
  return `${inputBase} ${error ? 'border-brick focus:border-brick' : 'border-stone-line focus:border-forest'}`;
}
export function EstimateModal({
  open,
  initialService,
  onClose
}: EstimateModalProps) {
  const form = useEstimateForm(open, initialService);
  const {
    values,
    errors,
    step
  } = form;
  const nameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => nameRef.current?.focus(), 50);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
      window.clearTimeout(t);
    };
  }, [open, onClose]);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const firstError = form.submit();
    if (firstError) document.getElementById(`estimate-${firstError}`)?.focus();
  };
  return <AnimatePresence data-id="element-62" data-mp="18b2mqe9qcoo9">
      {open && <motion.div className="fixed inset-0 z-[1200] flex items-end justify-center sm:items-center sm:p-6" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} data-id="element-63" data-mp="13ekjjiu1sye1">
          <div className="absolute inset-0 bg-forest-dark/60" onClick={onClose} aria-hidden="true" data-id="element-64" data-mp="3d63s956c1hu2" />
          <motion.div role="dialog" aria-modal="true" aria-labelledby="estimate-title" className="relative flex max-h-[92vh] w-full max-w-xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl" initial={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.22,
        ease
      }} data-id="element-65" data-mp="3h7gw59pv7ayc">
            <div className="flex items-start justify-between gap-4 border-b border-stone-line px-6 py-5" data-id="element-66" data-mp="8rx6slsbecok">
              <div data-id="element-67" data-mp="dr041k21zelz">
                <h2 id="estimate-title" className="font-display text-2xl font-bold text-forest" data-id="element-68" data-mp="dq72qylv5hb8">
                  {step === 'form' ? 'Get Your Free Estimate' : step === 'send' ? 'Send Your Request' : 'Request Sent'}
                </h2>
                <p className="mt-0.5 text-sm text-ink-muted" data-id="element-69" data-mp="iwdir42ob9n3">
                  {step === 'form' ? 'Takes about a minute. No obligation.' : step === 'send' ? 'Choose how to send it — it goes straight to Dany.' : 'Thanks! We’ll get back to you shortly.'}
                </p>
              </div>
              <button onClick={onClose} aria-label="Close" className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-ink-muted transition-colors duration-150 hover:bg-stone-soft hover:text-ink" data-id="element-70" data-mp="4ayliqtpb83">
                <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-71" data-mp="1pkskd71evrht" />
              </button>
            </div>

            <div className="overflow-y-auto" data-id="element-72" data-mp="1liffslyehzmm">
              {step === 'form' && <form onSubmit={handleSubmit} noValidate className="space-y-5 px-6 py-6" data-id="element-73" data-mp="1sm57k3159vxe">
                  <div className="grid gap-4 sm:grid-cols-2" data-id="element-74" data-mp="1xkyne23wlz0q">
                    <div data-id="element-75" data-mp="1xk6zzekgn7lj">
                      <label htmlFor="estimate-name" className="mb-1.5 block text-sm font-medium text-ink" data-id="element-76" data-mp="1jl7c494ruwqf">
                        Name
                      </label>
                      <input ref={nameRef} id="estimate-name" autoComplete="name" value={values.name} onChange={e => form.setField('name', e.target.value)} className={fieldClass(errors.name)} placeholder="Your full name" aria-invalid={!!errors.name} data-id="element-77" data-mp="1nl7rpx3lxdz2" />
                      {errors.name && <p className="mt-1 text-xs text-brick" data-id="element-78" data-mp="qxumtr788atf">{errors.name}</p>}
                    </div>
                    <div data-id="element-79" data-mp="vubf8xlglbcb">
                      <label htmlFor="estimate-phone" className="mb-1.5 block text-sm font-medium text-ink" data-id="element-80" data-mp="vxpcjrf9mucw">
                        Phone
                      </label>
                      <input id="estimate-phone" type="tel" inputMode="tel" autoComplete="tel" value={values.phone} onChange={e => form.setField('phone', formatPhone(e.target.value))} className={fieldClass(errors.phone)} placeholder="(214) 555-0123" aria-invalid={!!errors.phone} data-id="element-81" data-mp="7c1g349m29zc" />
                      {errors.phone && <p className="mt-1 text-xs text-brick" data-id="element-82" data-mp="150b9wvz8trfd">{errors.phone}</p>}
                    </div>
                    <div data-id="element-83" data-mp="1dz6gwaa435jq">
                      <label htmlFor="estimate-city" className="mb-1.5 block text-sm font-medium text-ink" data-id="element-84" data-mp="1dyewlvlysu8r">
                        City
                      </label>
                      <select id="estimate-city" value={values.city} onChange={e => form.setField('city', e.target.value)} className={fieldClass(errors.city)} aria-invalid={!!errors.city} data-id="element-85" data-mp="18y92m8s82vni">
                        <option value="" data-id="element-86" data-mp="kec5mv9u7hvt">Select your city</option>
                        {serviceAreas.map(c => <option key={c} value={c} data-id="element-87" data-mp="pcdq9kmwvp9e">
                            {c}
                          </option>)}
                        <option value="Other" data-id="element-88" data-mp="zbczp5fi3oxg">Other nearby</option>
                      </select>
                      {errors.city && <p className="mt-1 text-xs text-brick" data-id="element-89" data-mp="13cogclgeczti">{errors.city}</p>}
                    </div>
                    <div data-id="element-90" data-mp="3137wyg10fqw5">
                      <label htmlFor="estimate-address" className="mb-1.5 block text-sm font-medium text-ink" data-id="element-91" data-mp="313rcxr73o57j">
                        Address <span className="font-normal text-ink-muted" data-id="element-92" data-mp="354l3ksbxfy44">(optional)</span>
                      </label>
                      <input id="estimate-address" autoComplete="street-address" value={values.address} onChange={e => form.setField('address', e.target.value)} className={fieldClass()} placeholder="Street address" data-id="element-93" data-mp="2xmvbmt0tcgif" />
                    </div>
                  </div>

                  <fieldset data-id="element-94" data-mp="3bk1raeq8pz3f">
                    <legend className="mb-2 text-sm font-medium text-ink" data-id="element-95" data-mp="3blv0a0obsvel">What do you need?</legend>
                    <div id="estimate-services" tabIndex={-1} className="flex flex-wrap gap-2 focus:outline-none" data-id="element-96" data-mp="37hn7nseqyvb3">
                      {serviceOptions.map(s => {
                  const active = values.services.includes(s);
                  return <button key={s} type="button" aria-pressed={active} onClick={() => form.toggleService(s)} className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors duration-150 ${active ? 'border-forest bg-forest text-white' : 'border-stone-line bg-white text-ink hover:border-forest/40'}`} data-id="element-97" data-mp="1r46t684rgc6m">
                            {active && <CheckIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-98" data-mp="429oqeepvabd" />}
                            {s}
                          </button>;
                })}
                    </div>
                    {errors.services && <p className="mt-1.5 text-xs text-brick" data-id="element-99" data-mp="wq5jhdkq6s5i">{errors.services}</p>}
                  </fieldset>

                  <div data-id="element-100" data-mp="11j8elq55i9nt">
                    <label htmlFor="estimate-details" className="mb-1.5 block text-sm font-medium text-ink" data-id="element-101" data-mp="11jgttooqxr26">
                      Project details <span className="font-normal text-ink-muted" data-id="element-102" data-mp="2h980yor7cu16">(optional)</span>
                    </label>
                    <textarea id="estimate-details" rows={3} value={values.details} onChange={e => form.setField('details', e.target.value)} className={`${fieldClass()} resize-none`} placeholder="Size of the area, timeline, anything we should know" data-id="element-103" data-mp="2v0mi0q1jul5l" />
                  </div>

                  <PhotoUpload photos={form.photos} error={form.photoError} onAdd={form.addPhotos} onRemove={form.removePhoto} data-id="element-104" data-mp="2cbg73p6zyxuc" />

                  <button type="submit" className="w-full rounded-lg bg-brick px-6 py-3.5 text-base font-semibold text-white transition-[background-color,transform] duration-150 ease-out hover:bg-brick-dark active:scale-[0.98]" data-id="element-105" data-mp="3nqv3aqrdtloe">
                    Continue
                  </button>
                  <p className="text-center text-xs text-ink-muted" data-id="element-106" data-mp="srizg9pvn4kj">
                    Prefer to talk?{' '}
                    <a href={business.phoneHref} className="font-semibold text-forest" data-id="element-107" data-mp="opo3lkqptje5">
                      Call {business.phoneDisplay}
                    </a>
                  </p>
                </form>}

              {step === 'send' && <div className="space-y-5 px-6 py-6" data-id="element-108" data-mp="9tgeq74d3tsj">
                  <div className="rounded-xl bg-stone-soft p-4" data-id="element-109" data-mp="9tylhxonhqep">
                    <p className="mb-2 text-xs font-semibold text-ink-muted" data-id="element-110" data-mp="5smtjlib0t3c">Your message</p>
                    <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-ink" data-id="element-111" data-mp="247zise4jy5co">{form.message}</pre>
                    {form.photos.length > 0 && <div className="mt-3 flex gap-2 overflow-x-auto" data-id="element-112" data-mp="2041mscaixanu">
                        {form.photos.map(p => <img key={p.id} src={p.url} alt="" className="h-14 w-14 shrink-0 rounded-md border border-stone-line object-cover" data-id="element-113" data-mp="3n22dr68xps70" />)}
                      </div>}
                  </div>

                  {form.canShareFiles && <div data-id="element-114" data-mp="344rjk025toyf">
                      <button type="button" onClick={form.shareWithPhotos} disabled={form.sharing} className="flex w-full items-center justify-center gap-2 rounded-lg bg-forest px-5 py-3.5 font-semibold text-white transition-colors duration-150 hover:bg-forest-dark disabled:opacity-60" data-id="element-115" data-mp="343zvdaaw6ti6">
                        <ImageIcon className="h-5 w-5" aria-hidden="true" data-id="element-116" data-mp="sg9ypswxwhu0" />
                        {form.sharing ? 'Opening…' : 'Send with Photos'}
                      </button>
                      <p className="mt-2 text-center text-xs text-ink-muted" data-id="element-117" data-mp="2kf6yb2iap21l">
                        Choose Messages or WhatsApp, then send to{' '}
                        <span className="font-semibold text-ink" data-id="element-118" data-mp="2tgoeslzm75qt">{business.phoneDisplay}</span>
                      </p>
                    </div>}

                  {form.photos.length > 0 && !form.canShareFiles && <p className="rounded-lg border border-[#f5d27a] bg-[#fff8e1] px-4 py-3 text-sm text-ink" data-id="element-119" data-mp="20t28h5z05n09">
                      After your chat opens, tap the attach button to add your {form.photos.length}{' '}
                      {form.photos.length === 1 ? 'photo' : 'photos'}.
                    </p>}

                  <div className="grid gap-3 sm:grid-cols-2" data-id="element-120" data-mp="1my9mboolx8kp">
                    <a href={form.smsHref} onClick={() => form.setStep('done')} className="flex items-center justify-center gap-2 rounded-lg bg-brick px-5 py-3.5 font-semibold text-white transition-colors duration-150 hover:bg-brick-dark" data-id="element-121" data-mp="1i0gih2as0qnl">
                      <MessageSquareTextIcon className="h-5 w-5" aria-hidden="true" data-id="element-122" data-mp="3kprh4wmknplo" />
                      Send by Text
                    </a>
                    <a href={form.whatsappHref} target="_blank" rel="noopener noreferrer" onClick={() => form.setStep('done')} className="flex items-center justify-center gap-2 rounded-lg border border-forest bg-white px-5 py-3.5 font-semibold text-forest transition-colors duration-150 hover:bg-forest/5" data-id="element-123" data-mp="g96y9xqeiw5g">
                      <PhoneIcon className="h-5 w-5" aria-hidden="true" data-id="element-124" data-mp="2rxoe1qgrld30" />
                      Send via WhatsApp
                    </a>
                  </div>
                  <button type="button" onClick={() => form.setStep('form')} className="inline-flex items-center gap-1.5 text-sm font-medium text-ink-muted hover:text-ink" data-id="element-125" data-mp="3c8iukbq4gnck">
                    <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-126" data-mp="32ifu5qgo8747" />
                    Edit details
                  </button>
                </div>}

              {step === 'done' && <div className="flex flex-col items-center px-6 py-10 text-center" data-id="element-127" data-mp="3v4halu5wyv38">
                  <CheckCircle2Icon className="h-12 w-12 text-leaf" aria-hidden="true" data-id="element-128" data-mp="3q8jrjg39namr" />
                  <p className="mt-4 max-w-sm text-ink-muted" data-id="element-129" data-mp="3m4lurc1eybwv">
                    If your message app didn’t open, call or text us directly at{' '}
                    <a href={business.phoneHref} className="font-semibold text-forest" data-id="element-130" data-mp="2aqqhy9xzef8t">
                      {business.phoneDisplay}
                    </a>
                    .
                  </p>
                  <button type="button" onClick={onClose} className="mt-6 rounded-lg bg-forest px-6 py-3 font-semibold text-white transition-colors duration-150 hover:bg-forest-dark" data-id="element-131" data-mp="la4jhr3f9mhi">
                    Done
                  </button>
                </div>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}